import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, ChevronRight, History, LayoutGrid } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Tooltip } from 'antd';
import { cn } from '../../lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar';
import { NavDocuments } from './NavDocuments';
import styles from './AppSidebar.module.scss';
import useAuthStore from '../../store/authStore';
import { canAccess, MenuUrl } from '../../config/permissions';
import {
  ALL_APPS,
  APP_GROUPS,
  AppEntry,
  appEntryUrl,
  findAppByPath,
  HOME_PAGES,
  recordAppUse,
  searchApps,
  visiblePages,
} from './appMenu';

const RECENT_KEY = 'khothi.recentApps';
const RECENT_MAX = 4;

const readRecent = (): string[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> { }

export function AppSidebar({ ...props }: AppSidebarProps) {
  const { state } = useSidebar();
  const { permissions, isSuperAdmin } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [recent, setRecent] = useState<string[]>(readRecent);

  // Ẩn mục chưa có trang ('/#') và mục người dùng không có quyền truy cập
  const isVisible = (url: MenuUrl) => url !== '/#' && canAccess(url, permissions, isSuperAdmin);

  // App đang mở chỉ tính từ URL — bấm lại đúng app đang mở cũng không làm mất sidebar
  const activeApp = findAppByPath(location.pathname);

  // Ghi nhận "Dùng gần đây" và số lần mở app (trang chủ xếp app hay dùng lên đầu)
  useEffect(() => {
    if (!activeApp) return;
    recordAppUse(activeApp.name);
    setRecent((prev) => {
      if (prev[0] === activeApp.name) return prev;
      const next = [activeApp.name, ...prev.filter((n) => n !== activeApp.name)].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* bỏ qua */ }
      return next;
    });
  }, [activeApp?.name]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsSearching(false);
    setSearchText('');
  };

  const go = (url: MenuUrl) => {
    closeMenu();
    navigate(url);
  };

  // Sidebar dọc: trang con của app đang mở (hoặc menu Trang chủ khi URL không thuộc app nào)
  const sidebarPages = activeApp ? visiblePages(activeApp, isVisible) : HOME_PAGES.filter((p) => isVisible(p.url));
  const visibleMenuItems = sidebarPages.map((p) => ({ type: 'item' as const, title: p.title, url: p.url, icon: p.icon, activePrefixes: p.activePrefixes }));

  // Drawer: app hiện khi người dùng vào được ít nhất 1 trang của app
  const visibleGroups = APP_GROUPS
    .map((g) => ({ ...g, apps: g.apps.filter((a) => appEntryUrl(a, isVisible)) }))
    .filter((g) => g.apps.length > 0);

  const recentApps = recent
    .map((name) => ALL_APPS.find((a) => a.name === name))
    .filter((a): a is AppEntry => !!a && !!appEntryUrl(a, isVisible));

  // Ctrl + K / ⌘ + K: mở tìm nhanh ở mọi trang
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsMenuOpen(true);
        setIsSearching(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(
    () => searchApps(searchText, isVisible),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText, permissions, isSuperAdmin]
  );

  const renderAppTile = (app: AppEntry) => {
    const url = appEntryUrl(app, isVisible);
    const isActive = activeApp?.name === app.name;
    return (
      <button
        type="button"
        key={app.name}
        className="flex flex-col items-center cursor-pointer group bg-transparent border-0 p-0"
        onClick={() => url && go(url)}
        title={app.name}
      >
        <div className={cn(
          'w-[48px] h-[48px] md:w-[52px] md:h-[52px] rounded-2xl flex items-center justify-center group-hover:shadow-md transition-all duration-200 transform group-hover:-translate-y-1',
          app.bg,
          isActive && 'ring-2 ring-offset-2 ring-[#3e91ab]'
        )}>
          <app.icon className={app.color} size={22} strokeWidth={1.5} />
        </div>
        <span className={cn(
          'text-[12px] md:text-[14px] mt-2 text-center leading-tight group-hover:text-[#3e91ab] transition-colors',
          isActive ? 'text-[#3e91ab] font-medium' : 'text-gray-600 dark:text-[#e5e5e5]'
        )}>
          {app.name}
        </span>
      </button>
    );
  };

  const renderSearchResults = () => {
    if (results.length === 0) {
      return (
        <div className="text-center text-gray-400 text-sm py-12">
          Không tìm thấy ứng dụng hoặc trang nào khớp “{searchText.trim()}”
        </div>
      );
    }
    return (
      <div className="flex flex-col">
        {results.map((r) => (
          <button
            type="button"
            key={`${r.app.name}-${r.page?.url ?? 'app'}`}
            onClick={() => go(r.url)}
            className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-[#262626] text-left bg-transparent border-0 cursor-pointer"
          >
            <span className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', r.app.bg)}>
              {r.page ? <r.page.icon className={r.app.color} size={18} strokeWidth={1.5} /> : <r.app.icon className={r.app.color} size={18} strokeWidth={1.5} />}
            </span>
            <span className="flex-1 min-w-0">
              {r.page ? (
                <>
                  <span className="block text-[14px] text-gray-700 dark:text-[#e5e5e5] truncate">{r.page.title}</span>
                  <span className="block text-[12px] text-gray-400 truncate">{r.app.name}</span>
                </>
              ) : (
                <>
                  <span className="block text-[14px] font-medium text-gray-700 dark:text-[#e5e5e5] truncate">{r.app.name}</span>
                  <span className="block text-[12px] text-gray-400">Ứng dụng</span>
                </>
              )}
            </span>
            <ChevronRight className="text-gray-300 shrink-0" size={16} />
          </button>
        ))}
      </div>
    );
  };

  const renderDrawer = (
    <Drawer
      placement="left"
      closable={false} // Giữ nguyên false để dùng header custom cho đẹp
      onClose={closeMenu}
      open={isMenuOpen}
      width={window.innerWidth < 768 ? '100%' : 468} // Ép 100% trên Mobile để không bị lỗi giao diện
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex justify-between items-center px-4 md:px-6 py-4 border-b border-gray-100 sticky top-0 bg-white dark:bg-[#1f1f1f] z-10 min-h-[73px]">
        {!isSearching ? (
          <>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => go('/')}>
              <img src="/images/logo-v.png" alt="logo" className='w-8 md:w-10 h-8 md:h-10 object-contain' />
            </div>

            <div className="flex items-center gap-5">
              <Search
                className="text-gray-400 cursor-pointer hover:text-[#3e91ab] transition-colors"
                size={20}
                onClick={() => setIsSearching(true)}
              />
              {/* Nút X chỉ hiện trên Mobile (block) và ẩn trên PC (md:hidden) */}
              <X
                className="text-gray-500 cursor-pointer hover:text-red-500 transition-colors md:hidden block"
                size={24}
                onClick={closeMenu}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center w-full gap-3 animate-in fade-in duration-200">
            <Search className="text-gray-400 shrink-0" size={20} />
            <input
              type="text"
              placeholder="Tìm ứng dụng hoặc trang (vd: phiếu chi)"
              className="flex-1 outline-none text-[15px] text-gray-700 bg-transparent placeholder:text-gray-400 dark:text-[#e5e5e5]"
              autoFocus
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && results[0]) go(results[0].url);
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setIsSearching(false);
                  setSearchText('');
                }
              }}
            />
            <X
              className="text-gray-400 cursor-pointer hover:text-red-500 transition-colors shrink-0"
              size={20}
              strokeWidth={1.5}
              onClick={() => {
                setIsSearching(false);
                setSearchText('');
              }}
            />
          </div>
        )}
      </div>

      <div className="p-4 md:p-6 overflow-y-auto no-scrollbar pb-10">
        {searchText.trim() ? renderSearchResults() : (
          <>
            {recentApps.length > 0 && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-400 mb-5 text-[13px] tracking-wider uppercase flex items-center gap-1.5">
                  <History size={14} /> Dùng gần đây
                </h3>
                <div className="grid grid-cols-4 gap-y-6 gap-x-2">
                  {recentApps.map(renderAppTile)}
                </div>
              </div>
            )}

            {visibleGroups.map((group) => (
              <div key={group.title} className="mb-8">
                <h3 className="font-bold text-000000e0 mb-5 text-[15px] md:text-[16px] tracking-wider uppercase">
                  {group.title}
                </h3>
                <div className="grid grid-cols-4 gap-y-6 gap-x-2">
                  {group.apps.map(renderAppTile)}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Drawer>
  );

  // Nút mở menu ứng dụng: icon lưới để người dùng nhận ra là bấm được
  // (trước đây chỉ là logo nên nhiều người không biết). Cao 64px cho thẳng hàng với header.
  const renderLauncher = (
    <Tooltip title="Mở danh sách ứng dụng" placement="right" mouseEnterDelay={0.4}>
      <button
        type="button"
        onClick={() => setIsMenuOpen(true)}
        aria-label="Mở danh sách ứng dụng"
        aria-haspopup="dialog"
        aria-expanded={isMenuOpen}
        className="group w-full h-full flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3e91ab] focus-visible:ring-inset"
      >
        <span className={cn(
          'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
          isMenuOpen
            ? 'bg-[#3e91ab] text-white'
            : 'bg-[#3e91ab]/10 text-[#3e91ab] group-hover:bg-[#3e91ab] group-hover:text-white'
        )}>
          <LayoutGrid size={20} strokeWidth={2} />
        </span>
      </button>
    </Tooltip>
  );

  // Danh sách các URL hoặc tiền tố URL của các page con mà bạn muốn ẨN sidebar dọc bên trái
  const hiddenSidebarPrefixes = [
    '/work-process-page',
    '/so-do-to-chuc',
    '/career-progression',
  ];

  const isSubPageHidden = hiddenSidebarPrefixes.some(prefix => location.pathname.startsWith(prefix));

  // XỬ LÝ ẨN KHUNG SIDEBAR KHI KHÔNG CÓ DỮ LIỆU HOẶC LÀ PAGE CON ĐẶC BIỆT
  if (visibleMenuItems.length === 0 || isSubPageHidden) {
    return (
      <>
        <div className="fixed top-0 left-0 z-40 w-[70px] h-16 bg-white dark:bg-[#171717] border-r border-b border-[#e5e5e5] dark:border-[#262626] shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
          {renderLauncher}
        </div>
        {renderDrawer}
      </>
    );
  }

  // TRƯỜNG HỢP CÓ DỮ LIỆU, HIỂN THỊ SIDEBAR BÌNH THƯỜNG
  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        styles.appSidebar,
        state === 'collapsed' ? styles.collapsed : styles.expanded
      )}
      {...props}
    >
      {/* Bỏ padding mặc định để nút mở menu lấp đúng 64px, thẳng hàng với header */}
      <SidebarHeader className="!p-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <div className={cn(styles.headerLink, 'h-[calc(4rem-1px)]')}>
              {renderLauncher}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="sidebarContent min-h-0 flex-1 !overflow-y-auto no-scrollbar">
        {visibleMenuItems.length > 0 && <NavDocuments items={visibleMenuItems} />}
      </SidebarContent>

      <SidebarFooter className="!px-1 !py-3 shrink-0">
        <Tooltip title="Về trang chủ" placement="right" mouseEnterDelay={0.3}>
          <button
            type="button"
            onClick={() => go('/')}
            aria-label="Về trang chủ"
            className="w-full flex justify-center bg-transparent border-0 pt-1 cursor-pointer opacity-90 hover:opacity-100 transition-opacity"
          >
            <img src="/images/logo-v.png" alt="Kho Thi" className="w-14 h-auto object-contain mix-blend-multiply dark:mix-blend-normal dark:rounded-lg" />
          </button>
        </Tooltip>
      </SidebarFooter>

      {renderDrawer}

      <style dangerouslySetInnerHTML={{
        __html: `
        /* 1. Đảm bảo khi Sidebar thu gọn (collapsed) đủ chiều rộng chứa chữ */
        :root {
            --sidebar-width-icon: 88px !important;
        }
        [data-sidebar="sidebar"][data-state="collapsed"] {
            width: 88px !important;
            min-width: 88px !important;
        }

        /* 2. Định dạng Flexbox để ép Icon và Text xếp dọc */
        .sidebarContent [data-sidebar="menu-button"],
        .sidebarContent a, 
        .sidebarContent button {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            height: auto !important;
            padding: 0 !important;
            gap: 6px !important;
        }
        
        /* Căn chỉnh kích thước Icon */
        .sidebarContent [data-sidebar="menu-button"] > svg,
        .sidebarContent a > svg,
        .sidebarContent button > svg {
            margin: 0 !important;
            width: 22px !important;
            height: 22px !important;
        }
        
        /* 3. ÉP CHỮ LUÔN HIỆN CẢ KHI SIDEBAR THU GỌN */
        .sidebarContent [data-sidebar="menu-button"] > span,
        .sidebarContent a > span,
        .sidebarContent button > span {
            /* Hiện đủ tên trang: tên dài tự xuống dòng, không cắt "…" */
            display: block !important;
            overflow: visible !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
            font-size: 9px !important;
            text-align: center !important;
            white-space: normal !important;
            line-height: 1.3 !important;
            margin: 0 !important;
            opacity: 1 !important; 
            position: static !important;
            transform: none !important;
            visibility: visible !important;
            width: auto !important;
            height: auto !important;
        }

        /* Ghi đè cực mạnh các class ẩn chữ của Shadcn UI khi ở trạng thái collapsed */
        .sidebarContent .group-data-\\[collapsible\\=icon\\]\\:hidden,
        .sidebarContent [data-state="collapsed"] span,
        [data-sidebar="sidebar"][data-state="collapsed"] .sidebarContent span {
            display: block !important;
            opacity: 1 !important;
            visibility: visible !important;
        }

        /* 4. Ẩn các icon mũi tên mở rộng (Chevron) vì giao diện dọc không phù hợp để chứa nó */
        .sidebarContent [data-sidebar="menu-action"],
        .sidebarContent [data-sidebar="menu-badge"] {
            display: none !important;
        }

        /* 5. TÙY CHỈNH TRẠNG THÁI ACTIVE */
        .sidebarContent [data-sidebar="menu-button"][data-active="true"],
        .sidebarContent a.active,
        .sidebarContent .active {
            background-color: transparent !important; 
            color: #3e91ab !important;                
        }
        
        .sidebarContent [data-sidebar="menu-button"][data-active="true"] > svg,
        .sidebarContent a.active > svg,
        .sidebarContent .active > svg {
            color: #3e91ab !important;                
        }
        
        .sidebarContent [data-sidebar="menu-button"][data-active="true"]:hover,
        .sidebarContent a.active:hover {
            background-color: transparent !important;
        }
      `}} />

    </Sidebar>
  );
}